
import { Component, Prop } from 'vue-property-decorator';

import * as PleaseApi from '@/common/api/api.pleased';
import * as PleasedModel from '@/common/api/models.pleased';
import Header from '@/components/Header.vue';
import Pagination from '@/components/Pagination.vue';
import TimeAgo from '@/components/TimeAgo.vue';
import { Bridge, sendToParent } from '@/common/bridge';
import { emitOpenMyRequest } from '@/common/eventBus';
import loading from '@/components/loading';
import MyRequestDetail from './MyRequestDetail.vue';
import { UserTicket } from '../../common/api/model';

import { namespace } from 'vuex-class';
import { PleasedUserInfo } from '@/common/types';
import { GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import { analytics, syncLogs, CMP_CONTEXTS, BTN_EVTS } from '@/common/logs';
import { getRecentResolvedTicket } from '@/common/userRequest';

const userModule = namespace('user');

enum stepEnum {
  Main,
  Detail
}

@Component({
  components: {
    Header,
    MyRequestDetail,
    Pagination,
    TimeAgo,
  },
  directives: {
    loading,
  },
  filters: {
    capitalize (word: string) {
      return word[0].toUpperCase() + word.substring(1);
    },
  },
})
export default class MyRequest extends mixins(LogPageEventMixin) {
  isEmpty = false;
  stepEnum = stepEnum;
  selectedTicketId: string = '';
  selectedTicket = {} as UserTicket;
  currentStep = stepEnum['Main'];
  userTickets: PleasedModel.ListTicketDetail[] = [];
  recentResolvedTicket: PleasedModel.ListTicketDetail | null = null;
  loading: boolean = false;
  pagination = {
    total: 0,
    offset: 0,
    limit: 15,
    currentPage: 1,
  }

  @Prop({ default: false }) readonly openDirectly!: boolean;
  @Prop({ default: false }) readonly fromHome!: boolean;

  @userModule.State('pleasedUser') pleasedUser!: PleasedUserInfo;

  readonly [PAGE_TRACKING] = PAGE_EVENTS.MY_REQUESTS_HOME;
  readonly CMP_CONTEXTS = CMP_CONTEXTS;

  mounted () {
    this.getUserTickets();
    this.getRecentResolvedTicketAndReview();
  }
  get sortedOpenRequests () {
    const openTicks = this.userTickets.filter(item => item.status !== 'closed' && item.status !== 'solved' && item.status !== 'CLOSED' && item.status !== 'RESOLVED');
    return openTicks.sort((a, b) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }
  get sortedClosedRequests () {
    const closedTicks = this.userTickets.filter(item => item.status === 'closed' || item.status === 'solved' || item.status === 'CLOSED' || item.status === 'RESOLVED');
    return closedTicks.sort((a, b) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }
  get totalPages () {
    const { total, limit } = this.pagination;
    return Math.ceil(total / limit);
  }
  get displayActiveRequests () {
    const { currentPage } = this.pagination;
    return currentPage === 1 ? true : !!this.sortedOpenRequests.length;
  }
  get displayInactiveRequests () {
    const { limit } = this.pagination;
    return this.sortedClosedRequests.length ? true : this.userTickets.length < limit;
  }
  created () {
    document.title = 'My Requests';
    this.$gtag.event(GA_EVENT_NAME_ENUM.MY_REQUESTS_INIT);

    if (!this.openDirectly) {
      window.history.pushState('forward', '', '#myrequest'); // For handling Back button

      // Listeners have to be used by reference
      const backListener = () => {
        emitOpenMyRequest(false);
        window.removeEventListener('popstate', backListener);
      };
      window.addEventListener('popstate', backListener);
    }
  }
  getUserTickets () {
    this.loading = true;
    return PleaseApi.listTickets({
      sortBy: 'STATUS',
      offset: this.pagination.offset,
      limit: this.pagination.limit,
      order: 'ASC',
      requesterId: this.pleasedUser.userId,
    }).then(res => {
      this.isEmpty = res.total === 0;
      this.pagination.total = res.total;
      this.userTickets = res.data;
      return res;
    }).finally(() => {
      this.loading = false;
    });
  }

  async getRecentResolvedTicketAndReview () {
    const data = await getRecentResolvedTicket(this.pleasedUser.userId);

    if (!data || !data.ticket || data.ticketReviews.length > 0) {
      return;
    }

    this.recentResolvedTicket = data.ticket;
  }

  handleChangePage (page: number) {
    analytics.trackClick({
      name: BTN_EVTS.MY_REQUEST_HOME_PAGINATE_BUTTON,
      metadata: { clickedPage: page },
    });

    this.pagination.currentPage = page;
    this.pagination.offset = ((page - 1) * this.pagination.limit);
    this.getUserTickets()
      .then(() => {
        this.scrollToTop();
      });
  }
  goMain () {
    this.currentStep = stepEnum['Main'];
  }
  async handleBack () {
    if (this.fromHome) { // From home is router
      this.$router.back();
      return;
    }

    if (this.openDirectly) { // if the user is come from Sportybet wap/ app, then need to send event to parent
      await syncLogs();
      sendToParent('CLOSE');
    } else {
      history.back();
    }
  }

  goDetail (id: string, ticket: UserTicket, { isOpen }: { isOpen: boolean }) {
    analytics.trackClick({
      name: BTN_EVTS.MY_REQUEST_DETAIL_SELECTED,
      selection: ticket.id,
      metadata: { ticketStatus: ticket.status },
    });

    // Go to active conversation.
    if (isOpen) {
      Bridge.openActiveConversation({
        id,
        title: ticket.subject,
        updatedAt: ticket.updatedAt,
      });
    } else {
      this.selectedTicket = ticket;
      this.selectedTicketId = id;
      this.currentStep = stepEnum['Detail'];
    }
  }
  scrollToTop () {
    const main = (this.$refs.main as HTMLDivElement);
    main.scrollTop = 0;
  }
  showRecentResolvedMark (ticket: PleasedModel.ListTicketDetail) {
    if (!this.recentResolvedTicket) {
      return false;
    }

    return ticket.id === this.recentResolvedTicket.id;
  }
}
